import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useIntegrationHealth, type BadgeState } from '@/hooks/useIntegrationHealth';
import { cn } from '@/lib/utils';

const COLOR_CLASS: Record<BadgeState['color'], string> = {
  green: 'bg-success',
  yellow: 'bg-warning',
  red: 'bg-destructive',
  unknown: 'bg-muted-foreground',
};

const STATE_LABEL: Record<BadgeState['color'], string> = {
  green: 'all passing',
  yellow: 'stale or partial',
  red: 'failure detected',
  unknown: 'no runs yet',
};

export function IntegrationHealthBadge() {
  const { data, isLoading } = useIntegrationHealth();
  const color = data?.color ?? 'unknown';

  const relativeTime = data?.lastRunAt
    ? formatDistanceToNow(new Date(data.lastRunAt), { addSuffix: true })
    : 'Never';

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="status"
            aria-label={`Integration health: ${STATE_LABEL[color]}. Click for details.`}
            className={cn(
              'w-2 h-2 rounded-full ml-1 flex-shrink-0',
              COLOR_CLASS[color],
              color === 'red' && 'shadow-[0_0_6px_rgba(239,68,68,0.5)]',
              isLoading && 'opacity-50'
            )}
          />
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-xs">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">
              Integration health · Last run: {relativeTime}
            </p>
            {data && (
              <div className="space-y-0.5">
                {(['ghl', 'meta', 'google_ads'] as const).map((p) => (
                  <p key={p} className="text-xs">
                    {p.toUpperCase().replace('_', ' ')}:{' '}
                    <span
                      className={
                        data.providers[p].status === 'ok'
                          ? 'text-success'
                          : data.providers[p].status === 'failed' ||
                            data.providers[p].status === 'timed_out'
                          ? 'text-destructive'
                          : 'text-muted-foreground'
                      }
                    >
                      {data.providers[p].status}
                    </span>
                  </p>
                ))}
              </div>
            )}
            {data?.sunsetWarnings.map((w) => (
              <p key={w.provider} className="text-xs text-warning flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Sunset: {w.provider} {w.api_version ?? ''} · {w.sunset_date} (in {w.days} days)
              </p>
            ))}
            <p className="text-xs font-semibold text-secondary pt-1">View integration health →</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
